import type { ResolvedAssets, VideoFormat, VideoSpec } from '@hyvay/video-engine';
import apiClient from './api';
import type { MediaAsset } from './studio.service';

/** A media-library file attached to a video (videos carry their length). */
export type AttachedMedia = MediaAsset & { durationSec: number | null; proxyUrl: string | null };

/** AI turn running in the background (null when idle). */
export interface VideoTurn {
    status: 'running';
    stage: string;
    startedAt: string;
}

export interface VideoConfig {
    chatCost: number;
    renderCost: number;
    aiEnabled: boolean;
    voiceEnabled: boolean;
    renderEnabled: boolean;
    /** Narrator voices (empty when TTS is off); spec.voice.voiceId picks one. */
    voices: { id: string; name: string; meta: string }[];
    apps: { id: string; name: string; thumbnailUrl: string | null; published: boolean }[];
}

export interface VideoRender {
    id: string;
    status: 'QUEUED' | 'RENDERING' | 'DONE' | 'FAILED';
    progress: number;
    url: string | null;
    bytes: number | null;
    error: string | null;
    /** Aspect it was rendered as (null on renders made before this was stored). */
    format: VideoFormat | null;
    /** Length in seconds (null on renders made before this was stored). */
    durationSec: number | null;
    createdAt: string;
    finishedAt: string | null;
}

export interface VideoMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    /** Assistant turns: what the agent did and how long each step took. */
    steps?: { label: string; ms: number }[] | null;
    createdAt: string;
}

export interface VideoProjectDetail {
    id: string;
    name: string;
    spec: VideoSpec;
    assets: ResolvedAssets;
    sourceApplicationId: string | null;
    hasSite: boolean;
    turn: VideoTurn | null;
    media: AttachedMedia[];
    messages: VideoMessage[];
    renders: VideoRender[];
}

export interface VideoListItem {
    id: string;
    name: string;
    format: VideoFormat;
    scenes: number;
    spec: VideoSpec;
    updatedAt: string;
    lastRender: VideoRender | null;
    /** Newest finished render (the card's Lihat / Posting / Unduh). */
    lastDone: VideoRender | null;
}

const data = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

/** Player needs absolute URLs; the API returns music paths relative to its base. */
export function absoluteAssets(assets: ResolvedAssets): ResolvedAssets {
    const base = (apiClient.defaults.baseURL || '').replace(/\/+$/, '');
    const musicUrl = assets.musicUrl && assets.musicUrl.startsWith('/') ? `${base}${assets.musicUrl}` : assets.musicUrl;
    return { ...assets, musicUrl };
}

export const videoService = {
    config: () => data<VideoConfig>(apiClient.get('/video/config')),
    list: () => data<VideoListItem[]>(apiClient.get('/video')),
    /** Library files attached to any of the user's videos (not the whole library). */
    media: () => data<MediaAsset[]>(apiClient.get('/video/media')),
    create: (body: { name?: string; format?: VideoFormat; applicationId?: string; assetIds?: string[] }) => data<{ id: string }>(apiClient.post('/video', body)),
    get: (id: string) => data<VideoProjectDetail>(apiClient.get(`/video/${id}`)),
    update: (id: string, body: { name?: string; spec?: VideoSpec }) =>
        data<{ name: string; spec: VideoSpec; assets: ResolvedAssets }>(apiClient.patch(`/video/${id}`, body)),
    remove: (id: string) => data<{ deleted: boolean }>(apiClient.delete(`/video/${id}`)),
    /** Starts the AI turn in the background; poll get() until `turn` is null. */
    chat: (id: string, message: string) => data<{ turn: VideoTurn }>(apiClient.post(`/video/${id}/chat`, { message })),
    attach: (id: string, assetIds: string[]) => data<AttachedMedia[]>(apiClient.post(`/video/${id}/media`, { assetIds })),
    detach: (id: string, assetId: string) => data<AttachedMedia[]>(apiClient.delete(`/video/${id}/media/${assetId}`)),
    /** `format` renders another aspect ratio (e.g. 9:16 for TikTok) without changing the project. */
    render: (id: string, format?: VideoFormat) => data<VideoRender>(apiClient.post(`/video/${id}/render`, format ? { format } : {})),
};

/** Human message from an axios error (Nest puts it in response.data.message). */
export function errorText(e: any, fallback = 'Terjadi kesalahan.'): string {
    const m = e?.response?.data?.message;
    if (m === 'Insufficient credits') return 'Credit tidak cukup. Top up dulu, ya.';
    return (Array.isArray(m) ? m[0] : m) || e?.message || fallback;
}
